import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Plus, Menu } from "lucide-react";
import MessageList from "./MessageList";
import ChatSidebar from "./ChatSidebar";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
}

export default function ChatInterface() {
  const [conversations, setConversations] = useState<Conversation[]>([
    {
      id: "1",
      title: "New Chat",
      messages: [
        {
          id: "1",
          role: "assistant",
          content:
            "Hello! I'm your AI coding assistant. I can help you with code generation, debugging, and explanations. What would you like to work on?",
          timestamp: new Date(),
        },
      ],
      createdAt: new Date(),
    },
  ]);

  const [currentConvId, setCurrentConvId] = useState("1");
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(true);

  const currentConv = conversations.find((c) => c.id === currentConvId)!;

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue,
      timestamp: new Date(),
    };

    setConversations(
      conversations.map((conv) =>
        conv.id === currentConvId
          ? { ...conv, messages: [...conv.messages, newMessage] }
          : conv
      )
    );

    setInputValue("");

    // Simulate assistant response
    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          "I received your message. How can I assist you with coding today?",
        timestamp: new Date(),
      };

      setConversations(
        conversations.map((conv) =>
          conv.id === currentConvId
            ? { ...conv, messages: [...conv.messages, assistantMessage] }
            : conv
        )
      );
    }, 500);
  };

  const createNewConversation = () => {
    const newId = (conversations.length + 1).toString();
    const newConv: Conversation = {
      id: newId,
      title: "New Chat",
      messages: [
        {
          id: "1",
          role: "assistant",
          content:
            "Hello! I'm your AI coding assistant. What would you like to work on?",
          timestamp: new Date(),
        },
      ],
      createdAt: new Date(),
    };
    setConversations([newConv, ...conversations]);
    setCurrentConvId(newId);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <ChatSidebar
        conversations={conversations}
        currentConvId={currentConvId}
        onSelectConv={setCurrentConvId}
        onNewChat={createNewConversation}
        isOpen={isOpen}
        onToggle={setIsOpen}
      />

      {/* Main Chat Area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4 z-10">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 hover:bg-muted rounded-lg lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">{currentConv.title}</h2>
          <Button
            onClick={createNewConversation}
            size="sm"
            variant="outline"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Chat</span>
          </Button>
        </div>

        {/* Messages */}
        <MessageList messages={currentConv.messages} />

        {/* Input Area */}
        <div className="border-t border-border bg-background p-6">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask me anything about coding..."
              className="flex-1 text-base"
            />
            <Button
              type="submit"
              disabled={!inputValue.trim()}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Send</span>
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}